import { Currency, Price, Token } from '@kodiak-finance/sdk-core'
import { ButtonSecondary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import QuestionHelper from 'components/QuestionHelper'
import { RowFixed } from 'components/Row'
import { RowBetween } from 'components/Row'
// import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Text } from 'rebass'
import { Bound } from 'state/mint/v3/actions'
import styled from 'styled-components'

const ToggleSection = styled(RowBetween)`
  cursor: pointer;
  padding: 12px 0px;
  align-items: center;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 10px 0px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    padding: 8px 0px;
  `};
`

const ToggleContent = styled(RowFixed)`
  width: 100%;
  align-items: center;

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    gap: 4px;
  `};
`

const TextContainer = styled(RowBetween)`
  align-items: center;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 4px;

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    align-items: center;
  `};
`

const BetaLabel = styled(Text)`
  white-space: nowrap;
  margin-left: 4px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 9px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-size: 8px;
    margin-left: 0px;
    margin-top: -2px;
  `};
`

const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 10px;

  svg {
    ${({ theme }) => theme.mediaWidth.upToMedium`
      width: 16px;
      height: 16px;
    `};

    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
      width: 14px;
      height: 14px;
    `};
  }
`

const Separator = styled.div`
  width: 140px;
  height: 1px;
  background-color: ${({ theme }) => theme.deprecated_bg3};

  ${({ theme }) => theme.mediaWidth.upToMedium`
    width: 300px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    width: 100px;
  `};
`

const InfoRow = styled(RowBetween)`
  padding: 8px 0;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 6px 0;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    padding: 4px 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  `};
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 13px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-size: 12px;
  `};
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  font-weight: 500;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 13px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-size: 12px;
  `};
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    gap: 6px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    gap: 4px;
    flex-direction: column;
    width: 100%;
    
    button {
      width: 100%;
    }
  `};
`

interface AdvancedDeploySectionProps {
  priceLower?: Price<Token, Token>
  priceUpper?: Price<Token, Token>
  currencyA?: Currency | null
  currencyB?: Currency | null
  ticks?: {
    LOWER?: number
    UPPER?: number
  }
  ticksAtLimit: { [bound in Bound]?: boolean | undefined }
  isNoLiquidity?: boolean
  onDeployIsland: () => Promise<any>
}

const AdvancedDeploySection = ({
  priceLower,
  priceUpper,
  currencyA,
  currencyB,
  ticks,
  ticksAtLimit,
  isNoLiquidity,
  onDeployIsland,
}: AdvancedDeploySectionProps) => {
  // const navigate = useChainAwareNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [deployedAddress, setDeployedAddress] = useState<string>()
  const [isDeploying, setIsDeploying] = useState(false)

  const tokenA = (currencyA ?? undefined)?.wrapped
  const tokenB = (currencyB ?? undefined)?.wrapped
  const isSorted = tokenA && tokenB && tokenA.sortsBefore(tokenB)

  const leftPrice = isSorted ? priceLower : priceUpper?.invert()
  const rightPrice = isSorted ? priceUpper : priceLower?.invert()

  const handleDeploy = async () => {
    setIsDeploying(true)
    try {
      // Call the original onDeployIsland which should return the transaction
      const tx = await onDeployIsland()
      // Wait for transaction confirmation
      const receipt = await tx.wait()

      // Find the IslandCreated event in the logs
      const islandCreatedEvent = receipt.events?.find((event: { event: string }) => event.event === 'IslandCreated')

      if (islandCreatedEvent?.args?.island) {
        // The island address is the third parameter in the event
        setDeployedAddress(islandCreatedEvent.args.island)
      }
    } catch (error) {
      console.error('Deployment failed:', error)
    }
    setIsDeploying(false)
  }

  // Create Farm button temporarily disabled
  // const navigateToFarm = () => {
  //   if (deployedAddress) {
  //     navigate(`/farms/v2-deploy?stakingToken=${deployedAddress}`)
  //   }
  // }

  return (
    <AutoColumn gap="md">
      <ToggleSection onClick={() => setIsExpanded(!isExpanded)}>
        <Separator />
        <ToggleContent gap="8px">
          <TextContainer gap="2px">
            <Text fontWeight={500} fontSize={14}>
              Advanced Mode
            </Text>
            <BetaLabel fontSize={10} color="orange">
              (beta)
            </BetaLabel>
          </TextContainer>
          <ChevronContainer>{isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</ChevronContainer>
        </ToggleContent>
        <Separator />
      </ToggleSection>

      {isExpanded && (
        <LightCard>
          <AutoColumn gap="lg">
            <RowBetween>
              <Text fontWeight={500} fontSize={16}>
                Deploy fixed range permissionless Island
              </Text>
              <QuestionHelper text="Deployed Islands are private by default and only accessible via direct contract address. They become discoverable once whitelisted or associated with a farming incentive program. After deployment, you'll be able to navigate directly to create a farm for this Island." />
            </RowBetween>
            <AutoColumn gap="sm">
              <InfoRow>
                <Label>Price Range:</Label>
                <Value>
                  {ticksAtLimit[isSorted ? Bound.LOWER : Bound.UPPER] ? '0' : leftPrice?.toSignificant(5)} {' / '}
                  {ticksAtLimit[isSorted ? Bound.UPPER : Bound.LOWER] ? '∞' : rightPrice?.toSignificant(5)}
                </Value>
              </InfoRow>
              <InfoRow>
                <Label>Tick Range:</Label>
                <Value>{ticks?.LOWER && ticks?.UPPER ? `${ticks.LOWER} / ${ticks.UPPER}` : '-'}</Value>
              </InfoRow>
              {/* 
              <InfoRow>
                <Label>Manager:</Label>
                <Value>0x</Value>
              </InfoRow> */}
            </AutoColumn>

            {!isNoLiquidity && (
              <ButtonGroup>
                <ButtonSecondary onClick={handleDeploy} disabled={isDeploying}>
                  {isDeploying ? 'Deploying...' : 'Deploy Island'}
                </ButtonSecondary>
                {/* Create Farm button temporarily disabled
                {deployedAddress && <ButtonSecondary onClick={navigateToFarm}>Create Farm</ButtonSecondary>} */}
              </ButtonGroup>
            )}
          </AutoColumn>
        </LightCard>
      )}
    </AutoColumn>
  )
}

export default AdvancedDeploySection
